import { useContext, useEffect, useState, useMemo, useCallback } from "react";

//Components
import {
  FormControl,
  Modal,
  TextField,
  Button,
  FormHelperText,
} from "@material-ui/core";
import Autocomplete, {
  createFilterOptions,
} from "@material-ui/lab/Autocomplete";

//Context
import { UserContext } from "../Contexts/UserContext";
import { IncidentsContext } from "../Contexts/IncidentsContext";
import { INCIDENT_ACTIONS } from "../Actions/IncidentActions";

//Fetching
import { useLazyQuery, useMutation } from "@apollo/client";
import {
  VEHICLE_VIN_AUTO_SUGGEST,
  VEHICLE_PLATE_AUTO_SUGGEST,
} from "../Queries/VehicleAutoSuggests";
import { GET_VEHICLE_BY_VIN } from "../Queries/GetVehicleByVin";
import { GET_VEHICLE_BY_PLATE } from "../Queries/GetVehicleByPlate";
import { ADD_OR_UPDATE_VEHICLE_IN_INCIDENT } from "../Mutations/IncidentMutations/UpdateVehicleInIncident";

//Styles
import { useStyles } from "../Styles/styles";

//Utils
import { handleUuid } from "../utils/utils";
import { MutationContext } from "../Contexts/MutationContext";
import { shouldBeLocked } from "../utils/Roles";
import { validateVin } from "../utils/vindecoder";
import clsx from "clsx";
import CheckboxWithLabel from "./CheckboxWithLabel";
import { VIN_DECODE_RESULT } from "../Queries/VinDecodeResult";

const VehicleAutoSuggest = ({
  title,
  vehicle,
  incidentId,
  incidentIndex,
  visId,
  visIndex,
  type,
  userRoles,
  disabled = undefined,
  ...props
}) => {
  const memoizedLocked = useMemo(
    () => shouldBeLocked(disabled, props?.canEdit, userRoles),
    [disabled, props?.canEdit, userRoles]
  );
  const { user, responseErrorHandler, onlineState } = useContext(UserContext);
  const { incidentDispatch } = useContext(IncidentsContext);
  const { updateVehiclePlate, updateVehicleAllFields } =
    useContext(MutationContext);
  const { UPDATE_VIS, UPDATE_VEHICLE } = INCIDENT_ACTIONS;
  const classes = useStyles();
  const filter = createFilterOptions();

  const [options, setOptions] = useState([]);
  const [showModal, setShowModal] = useState(false);
  const [addModal, setAddModal] = useState(false);
  const [valueToCreate, setValueToCreate] = useState(null);
  const [isCustomVin, setIsCustomVin] = useState(false);

  const handleValueName = useCallback(() => {
    if (type === "vin") {
      return vehicle?.vehicleVin?.name || null;
    } else if (type === "plate") {
      return vehicle?.vehiclePlate?.name || null;
    } else {
      return "";
    }
  }, [type, vehicle?.vehiclePlate?.name, vehicle?.vehicleVin?.name]);

  const [previousSearchValue, setPreviousSearchValue] = useState(
    handleValueName()
  );
  const [searchValue, setSearchValue] = useState(handleValueName());

  useEffect(() => {
    if (showModal) {
      setPreviousSearchValue(handleValueName);
      setSearchValue(handleValueName);
    }
  }, [handleValueName, showModal]);

  const [vehicleVinAutoSuggest] = useLazyQuery(VEHICLE_VIN_AUTO_SUGGEST, {
    context: { serializationKey: "incidentQueue" },
    variables: { originatorId: user?.id },
    onCompleted: data => {
      if (data) {
        responseErrorHandler(data);
        const res = data[Object.keys(data)[0]];
        setOptions(res);
        if (res.length === 0) {
          setPreviousSearchValue(searchValue);
        }
      }
    },
  });
  const [vehiclePlateAutoSuggest] = useLazyQuery(VEHICLE_PLATE_AUTO_SUGGEST, {
    context: { serializationKey: "incidentQueue" },
    variables: { originatorId: user?.id },
    onCompleted: data => {
      if (data) {
        responseErrorHandler(data);
        const res = data[Object.keys(data)[0]];
        setOptions(res);
        if (res.length === 0) {
          setPreviousSearchValue(searchValue);
        }
      }
    },
  });

  const [addOrUpdateVehicleInIncident] = useMutation(
    ADD_OR_UPDATE_VEHICLE_IN_INCIDENT,
    {
      onCompleted: data => {
        if (data) {
          responseErrorHandler(data);
          const res = data[Object.keys(data)[0]];
          const vis = res?.vehicleIncidentSpecific;
          const vehicle = vis?.vehicle;
          incidentDispatch({
            type: UPDATE_VIS,
            payload: {
              visId: visId,
              incidentId: incidentId,
              property: "vehicle",
              value: vehicle,
            },
          });
        }
      },
    }
  );

  const [getVehicleByVin] = useLazyQuery(GET_VEHICLE_BY_VIN, {
    context: { serializationKey: "incidentQueue" },
    variables: { originatorId: user?.id },
    onError: error => {
      console.error(JSON.stringify(error));
    },
    onCompleted: data => {
      if (data) {
        responseErrorHandler(data);
        const res = data[Object.keys(data)[0]];
        let input = {
          incidentId: incidentId,
          originatorId: user?.id,
          vehicleId: res.id,
          vehicleIncidentSpecificId: visId,
        };

        incidentDispatch({
          type: UPDATE_VIS,
          payload: {
            visId: visId,
            incidentId: incidentId,
            property: "vehicle",
            value: res,
          },
        });
        addOrUpdateVehicleInIncident({ variables: { input: input } });
      }
    },
  });

  const [getVehicleByPlate] = useLazyQuery(GET_VEHICLE_BY_PLATE, {
    context: { serializationKey: "incidentQueue" },
    variables: { originatorId: user?.id },
    onError: error => {
      console.error(JSON.stringify(error));
    },
    onCompleted: data => {
      if (data) {
        responseErrorHandler(data);
        const res = data[Object.keys(data)[0]];
        let input = {
          incidentId: incidentId,
          originatorId: user?.id,
          vehicleId: res.id,
          vehicleIncidentSpecificId: visId,
        };
        incidentDispatch({
          type: UPDATE_VIS,
          payload: {
            visId: visId,
            incidentId: incidentId,
            property: "vehicle",
            value: res,
          },
        });
        addOrUpdateVehicleInIncident({ variables: { input: input } });
      }
    },
  });

  const handleKeyBlur = event => {
    if (event.keyCode === 27 || event.keyCode === 13) {
      setShowModal(false);
    }
  };

  const [vinDecodeResult, { loading: vinLoading }] = useLazyQuery(
    VIN_DECODE_RESULT,
    {
      context: { serializationKey: "incidentQueue" },
      variables: { originatorId: user?.id },
      onError: error => {
        console.error(JSON.stringify(error));
      },
      onCompleted: data => {
        if (data) {
          responseErrorHandler(data);
          let input = {
            id: vehicle?.id,
            originatorId: user?.id,
            vehicleIncidentSpecificId: visId,
            incidentId: incidentId,
          };
          const { vehicleMake, year, vehicleModel } =
            data?.vinDecodeResult || {};
          // updateVehicleVin({ variables: { input: input } })
          input.vehicleMakeId = vehicleMake?.id;
          input.vehicleModelId = vehicleModel?.id;
          input.year = year;
          input.vehicleVinName = valueToCreate;
          input.vehicleVinId = handleUuid();
          updateVehicleAllFields({ variables: { input: input } });
          incidentDispatch({
            type: UPDATE_VEHICLE,
            payload: {
              incidentIndex: incidentIndex,
              visIndex: visIndex,
              property: "vehicleVin",
              value: { id: input.vehicleVinId, name: input.vehicleVinName },
            },
          });
          incidentDispatch({
            type: UPDATE_VEHICLE,
            payload: {
              incidentIndex: incidentIndex,
              visIndex: visIndex,
              property: "vehicleMake",
              value: vehicleMake,
            },
          });
          incidentDispatch({
            type: UPDATE_VEHICLE,
            payload: {
              incidentIndex: incidentIndex,
              visIndex: visIndex,
              property: "vehicleModel",
              value: vehicleModel,
            },
          });
          incidentDispatch({
            type: UPDATE_VEHICLE,
            payload: {
              incidentIndex: incidentIndex,
              visIndex: visIndex,
              property: "year",
              value: year,
            },
          });
          setValueToCreate("");
        }
      },
    }
  );

  const decodeVin = async vin => {
    if (validateVin(vin)) {
      if (onlineState) {
        vinDecodeResult({ variables: { vin: vin } });
      } else {
        let input = {
          id: vehicle?.id,
          originatorId: user?.id,
          vehicleIncidentSpecificId: visId,
          incidentId: incidentId,
        };
        input.vehicleVinName = valueToCreate;
        input.vehicleVinId = handleUuid();
        updateVehicleAllFields({ variables: { input: input } });
        incidentDispatch({
          type: UPDATE_VEHICLE,
          payload: {
            incidentIndex: incidentIndex,
            visIndex: visIndex,
            property: "vehicleVin",
            value: { id: input.vehicleVinId, name: input.vehicleVinName },
          },
        });
        setValueToCreate("");
      }
    } else {
      return { data: { errors: ["Not a valid VIN"] } };
    }
  };
  const createNewEntry = async () => {
    let input = {
      id: vehicle?.id,
      originatorId: user?.id,
      vehicleIncidentSpecificId: visId,
      incidentId: incidentId,
    };
    if (type === "vin") {
      input.vehicleVinName = valueToCreate?.toUpperCase();
      if (!isCustomVin) {
        decodeVin(valueToCreate?.toUpperCase());
      } else {
        updateVehicleAllFields({ variables: { input: input } });
        incidentDispatch({
          type: UPDATE_VEHICLE,
          payload: {
            incidentIndex: incidentIndex,
            visIndex: visIndex,
            property: "vehicleVin",
            value: {
              id: handleUuid(),
              name: input.vehicleVinName?.toUpperCase(),
            },
          },
        });
        setValueToCreate("");
      }
    }
    if (type === "plate") {
      input.vehiclePlateName = valueToCreate;
      updateVehiclePlate({ variables: { input: input } });
      incidentDispatch({
        type: UPDATE_VEHICLE,
        payload: {
          incidentIndex: incidentIndex,
          visIndex: visIndex,
          property: "vehiclePlate",
          value: { id: handleUuid(), name: valueToCreate?.toUpperCase() },
        },
      });
      setValueToCreate("");
    }
    setAddModal(false);
  };

  useEffect(() => {
    if (searchValue && searchValue.length > 1) {
      //If the new search value contains the previous search value that returned no results don't query again
      if (!searchValue.includes(previousSearchValue) && onlineState) {
        switch (type) {
          case "vin":
            vehicleVinAutoSuggest({
              variables: { input: searchValue, count: 20 },
            });
            return undefined;
          case "plate":
            vehiclePlateAutoSuggest({
              variables: { input: searchValue, count: 20 },
            });
            return undefined;
          default:
            alert(`Type not found: ${type}`);
            return undefined;
        }
      }
    }
  }, [
    searchValue,
    previousSearchValue,
    vehicleVinAutoSuggest,
    vehiclePlateAutoSuggest,
    onlineState,
    type,
  ]);

  const handleBlur = () => {
    if (searchValue === "") {
      // if (updateType === 'person') {
      //   updatePerson({ variables: { input: { personId: id, homeAddressId: "" } } })
      // }
      // if (updateType === 'incident') {
      //   updateCallInfo({ variables: { input: { incidentId: id, addressId: "", originatorId: user?.id } } })
      // }
    }
    setShowModal(false);
  };

  const handleAutoSuggestSelection = value => {
    //"" will clear input
    //check type and handle
    if (type === "vin" && !!value) {
      getVehicleByVin({ variables: { vin: value.match } });
    }
    if (type === "plate" && !!value) {
      getVehicleByPlate({ variables: { plate: value.match } });
    }
  };

  const handleAutoCompleteValue = () => {
    if (type === "vin") {
      return vehicle?.vehicleVin;
    }
    if (type === "plate") {
      return vehicle?.vehiclePlate;
    }
  };

  const valid =
    type === "vin" && valueToCreate && !isCustomVin
      ? validateVin(valueToCreate?.toUpperCase())
      : true;
  return vinLoading ? (
    <p>Decoding the VIN. Please wait...</p>
  ) : (
    <div
      style={{ display: "flex", flexDirection: "row", alignItems: "center" }}
    >
      <FormControl
        data-cy="narrative-full-width-form-control"
        fullWidth
        className={classes.formControl}
      >
        <Autocomplete
          // value={handleAutoCompleteValue() || searchValue}
          value={handleAutoCompleteValue()}
          onBlur={handleBlur}
          clearOnBlur={true}
          autoHighlight
          autoSelect={false}
          disabled={memoizedLocked}
          onKeyDown={handleKeyBlur}
          onInputChange={(event, newValue) => {
            setSearchValue(newValue?.toLocaleUpperCase());
          }}
          onChange={(event, newValue, reason) => {
            if (reason === "clear") {
              // handleAutoSuggestSelection("")
              let input = {
                id: vehicle?.id,
                originatorId: user?.id,
                vehicleIncidentSpecificId: visId,
                incidentId: incidentId,
              };
              let property;
              if (type === "vin") {
                input.vehicleVinId = "";
                property = "vehicleVin";
              }
              if (type === "plate") {
                input.vehiclePlateId = "";
                property = "vehiclePlate";
              }
              updateVehicleAllFields({ variables: { input: input } });
              incidentDispatch({
                type: UPDATE_VEHICLE,
                payload: {
                  incidentIndex: incidentIndex,
                  visIndex: visIndex,
                  property: property,
                  value: "",
                },
              });
            } else {
              if (
                typeof newValue === "object" &&
                newValue &&
                newValue?.action !== "Add"
              ) {
                handleAutoSuggestSelection(newValue);
                setShowModal(false);
              }
              if (newValue && newValue.action && newValue.action === "Add") {
                setAddModal(true);
                setValueToCreate(searchValue);
              }
            }
          }}
          filterOptions={(options, params) => {
            const filtered = options ? filter(options, params) : [];
            // Suggest the creation of a new value
            if (params.inputValue !== "") {
              filtered.push({
                inputValue: params.inputValue,
                value: `Add "${params.inputValue}"`,
                action: "Add",
              });
            }

            return filtered;
          }}
          selectOnFocus
          blurOnSelect={true}
          handleHomeEndKeys
          id={"vehicle-auto-suggest-" + handleUuid()}
          options={options}
          getOptionLabel={option => {
            // Value selected with enter, right from the input
            if (typeof option === "string") {
              return option;
            }
            // Add "xxx" option created dynamically
            if (option.inputValue) {
              return option.inputValue;
            }
            if (option.name) {
              return option.name;
            }
            if (option.value) {
              return option.value;
            }
          }}
          renderOption={option => option.value?.toUpperCase()}
          style={{ width: 300 }}
          freeSolo
          renderInput={params => {
            params.inputProps.value = params.inputProps.value?.toUpperCase();
            return (
              <TextField
                {...params}
                label={title}
                InputLabelProps={{ shrink: true }}
              />
            );
          }}
        />
      </FormControl>
      {/* } */}
      <Modal
        // disableEnforceFocus
        open={addModal}
        onClose={() => setAddModal(false)}
        className={classes.modal}
      >
        <div className={classes.paper}>
          <TextField
            id={"new-value"}
            label={title}
            value={valueToCreate || ""}
            onChange={e => setValueToCreate(e.target.value)}
            className={classes.modalInput}
          />
          {type === "vin" && (
            <CheckboxWithLabel
              userRoles={userRoles}
              {...props}
              checked={isCustomVin}
              onChange={e => setIsCustomVin(e.target.checked)}
              stateName={"isCustomVin"}
              title={"Non-Standard VIN?"}
            />
          )}
          {!valid && (
            <FormHelperText
              className={clsx(classes.leftMarginMatch, classes.redLabel)}
            >
              The VIN is not valid
            </FormHelperText>
          )}
          <br />
          <Button
            disabled={!valid}
            onClick={!memoizedLocked ? () => createNewEntry() : null}
          >
            Create {title}
          </Button>
          <Button
            onClick={() => {
              setValueToCreate(null);
              setAddModal(false);
            }}
          >
            Cancel
          </Button>
        </div>
      </Modal>
    </div>
  );
};

export default VehicleAutoSuggest;
